import { APP_CONSTANTS } from 'src/consumer/constants'
import { CANONICAL_ORIGIN } from 'src/shared/seo/SEOComponent'

/*
 * seoHelpers
 *
 * Central JSON-LD + canonical-URL builders shared by every public page.
 *
 * The goal is consistency — every page that calls SEOComponent should
 * be able to emit the right structured data (Article, MedicalCondition,
 * Person, Course, ItemList etc.) with a one-liner, rather than each
 * page hand-rolling its own schema.
 *
 * Every builder returns either a JSON-LD object or `null` if the
 * required fields are missing; SEOComponent tolerates null/undefined
 * entries and filters them before emitting, so callers can compose
 * several builders without null-guarding.
 */

// Canonical URL for a given pathname. Accepts absolute or relative
// paths and always returns a fully-qualified URL.
export function absoluteUrl(pathname) {
  if (!pathname) return CANONICAL_ORIGIN
  if (/^https?:\/\//i.test(pathname)) return pathname
  return `${CANONICAL_ORIGIN}${pathname.startsWith('/') ? pathname : '/' + pathname}`
}

// Organization identity — re-used by every page via `sameAs` and as the
// publisher of articles/podcasts.
export const ORGANIZATION_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'MedicalBusiness',
  '@id': `${CANONICAL_ORIGIN}/#organization`,
  name: 'AUMHUM',
  url: CANONICAL_ORIGIN,
  logo: APP_CONSTANTS.APP_LOGO_LINK,
  sameAs: [
    APP_CONSTANTS.PLAY_STORE_APP_LINK,
    APP_CONSTANTS.APP_STORE_APP_LINK,
    'https://www.youtube.com/@aumhumApp',
    'https://www.linkedin.com/company/aumhum',
    'https://www.instagram.com/aumhum.app/',
    'https://x.com/aumhums',
  ].filter(Boolean),
  medicalSpecialty: ['Psychiatry', 'Psychology', 'Endocrinology'],
  areaServed: ['IN', 'US', 'AE'],
  availableLanguage: ['en', 'hi'],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: APP_CONSTANTS.SUPPORT_EMAIL,
      telephone: '+91-63609-03465',
      availableLanguage: ['en', 'hi'],
    },
  ],
}

// WebSite with SearchAction — helps Google surface a site-wide search
// box on the brand SERP result for aumhum.com.
export const WEBSITE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${CANONICAL_ORIGIN}/#website`,
  name: 'AUMHUM',
  url: CANONICAL_ORIGIN,
  publisher: { '@id': `${CANONICAL_ORIGIN}/#organization` },
  potentialAction: {
    '@type': 'SearchAction',
    target: `${CANONICAL_ORIGIN}/therapy-psychiatry?search={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
}

// Breadcrumb list — takes an array of {name, path} and returns a
// BreadcrumbList JSON-LD object. Skips when the trail is empty.
export function buildBreadcrumbs(trail) {
  if (!trail || !trail.length) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((t, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: t.name,
      item: absoluteUrl(t.path),
    })),
  }
}

// Article JSON-LD for /article/:slug pages.
export function buildArticleJsonLd({
  title,
  description,
  image,
  pathname,
  authorName,
  datePublished,
  dateModified,
}) {
  if (!title || !pathname) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    image: image || APP_CONSTANTS.APP_LOGO_LINK,
    mainEntityOfPage: absoluteUrl(pathname),
    author: authorName
      ? { '@type': 'Person', name: authorName }
      : { '@id': `${CANONICAL_ORIGIN}/#organization` },
    publisher: { '@id': `${CANONICAL_ORIGIN}/#organization` },
    datePublished: datePublished || undefined,
    dateModified: dateModified || datePublished || undefined,
  }
}

// Person JSON-LD for /mentor/:slug profile pages.
export function buildPersonJsonLd({
  name,
  shortDesc,
  image,
  pathname,
  jobTitle,
  knowsAbout,
  languagesKnown,
}) {
  if (!name || !pathname) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name,
    description: shortDesc,
    image: image || APP_CONSTANTS.APP_LOGO_LINK,
    url: absoluteUrl(pathname),
    jobTitle: jobTitle || 'Clinician',
    worksFor: { '@id': `${CANONICAL_ORIGIN}/#organization` },
    knowsAbout: (knowsAbout || []).filter(Boolean),
    knowsLanguage: (languagesKnown || []).filter(Boolean),
  }
}

// MedicalCondition JSON-LD for /conditions/:slug.
export function buildMedicalConditionJsonLd({
  title,
  description,
  alternateNames,
  pathname,
  signs,
}) {
  if (!title || !pathname) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalCondition',
    name: title,
    alternateName: (alternateNames || []).filter(Boolean),
    description,
    url: absoluteUrl(pathname),
    signOrSymptom: (signs || [])
      .filter(Boolean)
      .slice(0, 8)
      .map((s) => ({ '@type': 'MedicalSignOrSymptom', name: s })),
    possibleTreatment: [
      { '@type': 'TherapeuticProcedure', name: 'Psychotherapy' },
      { '@type': 'TherapeuticProcedure', name: 'Psychiatric medication' },
      { '@type': 'TherapeuticProcedure', name: 'Combined pharmacotherapy and psychotherapy' },
    ],
  }
}

// MedicalTest JSON-LD for /assessment/:slug screener pages.
export function buildMedicalTestJsonLd({
  name,
  description,
  pathname,
  usedToDiagnose,
}) {
  if (!name || !pathname) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalTest',
    name,
    description,
    url: absoluteUrl(pathname),
    usedToDiagnose: usedToDiagnose
      ? { '@type': 'MedicalCondition', name: usedToDiagnose }
      : undefined,
  }
}

// ItemList JSON-LD for listing pages (articles, assessments, podcasts).
// Up to 20 items to avoid oversized payloads.
export function buildItemListJsonLd({ name, description, pathname, items }) {
  if (!name || !pathname || !items?.length) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    description,
    url: absoluteUrl(pathname),
    itemListElement: items.slice(0, 20).map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      url: absoluteUrl(it.path),
    })),
  }
}

// FAQPage JSON-LD — accepts array of {q, a}.
export function buildFaqJsonLd(questions) {
  const qs = (questions || []).filter((q) => q?.q && q?.a)
  if (!qs.length) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: qs.map((q) => ({
      '@type': 'Question',
      name: q.q,
      acceptedAnswer: { '@type': 'Answer', text: q.a },
    })),
  }
}

// PodcastSeries / PodcastEpisode JSON-LD for /podcast surfaces.
export function buildPodcastSeriesJsonLd({ name, description, pathname }) {
  if (!name) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'PodcastSeries',
    name,
    description,
    url: absoluteUrl(pathname || '/podcast'),
    webFeed: 'https://www.youtube.com/@aumhumApp',
    publisher: { '@id': `${CANONICAL_ORIGIN}/#organization` },
  }
}
